import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useNotify, useLogout } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { changePassword } from '@/services/manageService/userService';

export default function ChangePassword() {
    const notify = useNotify();
    const logout = useLogout();
    const navigate = useNavigate();

    const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,20}$/;

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const canSubmit = useMemo(() => {
        return oldPassword.trim() && newPassword.trim() && confirmPassword.trim();
    }, [oldPassword, newPassword, confirmPassword]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting) return;

        const oldPwd = oldPassword.trim();
        const newPwd = newPassword.trim();
        const confirmPwd = confirmPassword.trim();

        if (!oldPwd || !newPwd) {
            notify('Please input old password and new password', { type: 'warning' });
            return;
        }
        if (newPwd !== confirmPwd) {
            notify('Confirm password mismatch', { type: 'warning' });
            return;
        }

        if (!PASSWORD_REGEX.test(newPwd)) {
            notify('Password must contain uppercase, lowercase, number and special character, length 8-20.', { type: 'warning' });
            return;
        }

        setSubmitting(true);
        try {
            await changePassword({ oldPassword: oldPwd, newPassword: newPwd, confirmPassword: confirmPwd });
            notify('Password changed successfully. Please login again.', { type: 'info' });
            try {
                await logout();
            } catch {
                // ignore
            }
            navigate('/login', { replace: true });
        } catch (err: any) {
            notify(err?.message || 'Change password failed', { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', mt: 10 }}>
            <Paper sx={{ width: 420, p: 3 }} elevation={3}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Change Password
                </Typography>
                <form onSubmit={onSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            label="Old Password"
                            type="password"
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            fullWidth
                            autoComplete="current-password"
                        />
                        <TextField
                            label="New Password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            fullWidth
                            autoComplete="new-password"
                        />
                        <TextField
                            label="Confirm Password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            fullWidth
                            autoComplete="new-password"
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={!canSubmit || submitting}
                        >
                            {submitting ? 'Submitting...' : 'Confirm'}
                        </Button>
                    </Stack>
                </form>
            </Paper>
        </Box>
    );
}
